package com.schoolconnect.app.designsystem

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.layout.width
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.Modifier
import androidx.compose.ui.Alignment
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.graphics.Shape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.schoolconnect.app.designsystem.AppLabeledField
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.chevron_up
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource

/**
 * The shared popup menu surface used by option selectors and action menus.
 *
 * Field selectors should pass the measured field width through [modifier] so
 * the popup aligns exactly with the control that opened it.
 */
@Composable
fun AppDropdownMenu(
    expanded: Boolean,
    onDismissRequest: () -> Unit,
    modifier: Modifier = Modifier,
    width: Dp? = null,
    offset: DpOffset = DpOffset.Zero,
    containerColor: Color = AppSurface,
    // Controls & Menus spec: menus use an 8 px radius and restrained shadow.
    shape: Shape = RoundedCornerShape(8.dp),
    tonalElevation: Dp = 0.dp,
    shadowElevation: Dp = 8.dp,
    border: BorderStroke? = BorderStroke(1.dp, AppBorder),
    content: @Composable ColumnScope.() -> Unit
) {
    DropdownMenu(
        expanded = expanded,
        onDismissRequest = onDismissRequest,
        offset = offset,
        modifier = modifier.then(width?.let { Modifier.width(it) } ?: Modifier),
        containerColor = containerColor,
        shape = shape,
        tonalElevation = tonalElevation,
        shadowElevation = shadowElevation,
        border = border,
        content = content
    )
}

/**
 * Standard labeled select field for filters and short option lists.
 * The popup width follows the field so filter rows stay visually aligned.
 */
@Composable
fun AppLabeledDropdownField(
    label: String,
    value: String,
    options: List<String>,
    onSelected: (String) -> Unit,
    modifier: Modifier = Modifier,
    allOptionLabel: String = "All",
    includeAllOption: Boolean = true,
    // Controls & Menus spec: standard controls are 40 px high.
    fieldHeight: Dp = 40.dp,
    enabled: Boolean = true
) {
    var expanded by remember { mutableStateOf(false) }
    var anchorWidth by remember { mutableStateOf(0.dp) }
    val density = LocalDensity.current

    Column(
        modifier = modifier,
        verticalArrangement = if (label.isBlank()) Arrangement.Top else Arrangement.spacedBy(6.dp)
    ) {
        if (label.isNotBlank()) {
            Text(
                text = label,
                color = AppNavy,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold
            )
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(fieldHeight)
                .onGloballyPositioned { anchorWidth = with(density) { it.size.width.toDp() } }
                .background(AppSurface, RoundedCornerShape(6.dp))
                .border(1.dp, AppInputBorder, RoundedCornerShape(6.dp))
                .clickable(enabled = enabled) { expanded = true },
            contentAlignment = Alignment.CenterStart
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = value,
                    color = if (enabled) AppNavy else AppMuted,
                    fontSize = 14.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    painter = painterResource(if (expanded) Res.drawable.chevron_up else Res.drawable.chevron_down),
                    contentDescription = null,
                    tint = if (enabled) AppNavy else AppMuted,
                    modifier = Modifier.size(18.dp)
                )
            }
            AppDropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                width = anchorWidth.takeIf { it > 0.dp },
                containerColor = AppSurface,
                shape = RoundedCornerShape(8.dp),
                shadowElevation = 8.dp
            ) {
                if (includeAllOption) {
                    androidx.compose.material3.DropdownMenuItem(
                        text = { Text(allOptionLabel, color = AppNavy) },
                        onClick = { expanded = false; onSelected("") }
                    )
                }
                options.forEach { option ->
                    androidx.compose.material3.DropdownMenuItem(
                        text = { Text(option, color = AppNavy) },
                        onClick = { expanded = false; onSelected(option) }
                    )
                }
            }
        }
    }
}

/**
 * Reusable searchable dropdown field for long option lists.
 * The menu is anchored to the field, keeps the field width, and filters locally.
 */
@Composable
fun AppSearchableDropdownField(
    label: String,
    value: String,
    placeholder: String,
    options: List<String>,
    onValueChange: (String) -> Unit,
    required: Boolean = false,
    errorText: String = "",
    modifier: Modifier = Modifier,
    fieldHeight: Dp = 40.dp,
    valueFontWeight: FontWeight = FontWeight.SemiBold,
    maxMenuHeight: Dp = 420.dp,
    onSearchQueryChange: ((String) -> Unit)? = null,
    hasMore: Boolean = false,
    isLoadingMore: Boolean = false,
    onLoadMore: (() -> Unit)? = null
) {
    val fieldTextStyle = MaterialTheme.typography.bodyMedium.copy(
        fontSize = 14.sp,
        fontWeight = valueFontWeight
    )
    val optionTextStyle = MaterialTheme.typography.bodyMedium.copy(fontSize = 14.sp)
    var expanded by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val anchorWidth = with(LocalDensity.current) { anchorSize.width.toDp() }
    val filteredOptions = remember(query, options, onSearchQueryChange) {
        if (onSearchQueryChange != null) options else options.filter { it.contains(query.trim(), ignoreCase = true) }
    }

    val fieldContent: @Composable () -> Unit = {
        Box(modifier = Modifier.onGloballyPositioned { anchorSize = it.size }) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(fieldHeight)
                    .background(Color.White, RoundedCornerShape(6.dp))
                    .border(1.dp, if (errorText.isNotBlank()) AppDanger else AppInputBorder, RoundedCornerShape(6.dp))
                    .clickable { expanded = true }
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = value.ifBlank { placeholder },
                    style = fieldTextStyle.copy(
                        fontWeight = if (value.isBlank()) FontWeight.Normal else valueFontWeight
                    ),
                    color = if (value.isBlank()) AppMuted else AppNavy,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Icon(
                    painter = painterResource(if (expanded) Res.drawable.chevron_up else Res.drawable.chevron_down),
                    contentDescription = null,
                    tint = AppNavy,
                    modifier = Modifier.size(20.dp)
                )
            }

            if (expanded) {
                Popup(
                    popupPositionProvider = anchoredDropdownPositionProvider(),
                    onDismissRequest = {
                        expanded = false
                        query = ""
                    },
                    properties = PopupProperties(
                        focusable = true,
                        dismissOnBackPress = true,
                        dismissOnClickOutside = true
                    )
                ) {
                    Surface(
                        modifier = Modifier.width(anchorWidth.takeIf { it > 0.dp } ?: 280.dp),
                        shape = RoundedCornerShape(10.dp),
                        color = AppSurface,
                        border = BorderStroke(1.dp, AppBorder),
                        shadowElevation = 8.dp
                    ) {
                        Column {
                            ColumnScopeSearchField(
                                query = query,
                                onQueryChange = { query = it; onSearchQueryChange?.invoke(it) },
                                placeholder = "Search $label"
                            )
                            androidx.compose.material3.HorizontalDivider(color = AppBorder)
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .heightIn(max = maxMenuHeight)
                                    .verticalScroll(rememberScrollState())
                                    .padding(vertical = 8.dp)
                            ) {
                                if (filteredOptions.isEmpty()) {
                                    Text(
                                        text = "No matches found",
                                        style = optionTextStyle,
                                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
                                        color = AppMuted
                                    )
                                } else {
                                    filteredOptions.forEach { option ->
                                        Text(
                                            text = option,
                                            style = optionTextStyle,
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable {
                                                    onValueChange(option)
                                                    expanded = false
                                                    query = ""
                                                }
                                                .padding(horizontal = 16.dp, vertical = 11.dp),
                                            color = AppNavy,
                                            fontWeight = if (option == value) FontWeight.Bold else FontWeight.Medium
                                        )
                                    }
                                }
                                if (onLoadMore != null && hasMore) {
                                    Text(
                                        text = if (isLoadingMore) "Loading…" else "Load more",
                                        style = optionTextStyle,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable(enabled = !isLoadingMore) { onLoadMore() }
                                            .padding(horizontal = 16.dp, vertical = 12.dp),
                                        color = if (isLoadingMore) AppMuted else AppPrimary,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (label.isBlank() && !required) {
        Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Box(Modifier.fillMaxWidth()) { fieldContent() }
            if (errorText.isNotBlank()) {
                Text(
                    text = errorText,
                    color = AppDanger,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium
                )
            }
        }
    } else {
        AppLabeledField(
            label = label,
            required = required,
            errorText = errorText,
            verticalSpacing = 4.dp,
            modifier = modifier
        ) {
            fieldContent()
        }
    }
}

@Composable
private fun ColumnScopeSearchField(
    query: String,
    onQueryChange: (String) -> Unit,
    placeholder: String
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(10.dp)
            .height(38.dp)
            .background(Color.White, RoundedCornerShape(6.dp))
            .border(1.dp, AppInputBorder, RoundedCornerShape(6.dp))
            .padding(horizontal = 10.dp),
        contentAlignment = Alignment.CenterStart
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                painter = painterResource(Res.drawable.search),
                contentDescription = null,
                tint = AppMuted,
                modifier = Modifier.size(18.dp)
            )
            BasicTextField(
                value = query,
                onValueChange = onQueryChange,
                singleLine = true,
                textStyle = MaterialTheme.typography.bodyMedium.copy(
                    fontSize = 14.sp,
                    color = AppNavy,
                    fontWeight = FontWeight.SemiBold
                ),
                cursorBrush = SolidColor(AppPrimary),
                modifier = Modifier.padding(start = 8.dp).fillMaxWidth(),
                decorationBox = { innerTextField ->
                    if (query.isBlank()) {
                        Text(
                            placeholder,
                            style = MaterialTheme.typography.bodyMedium.copy(
                                fontSize = 14.sp,
                                fontWeight = FontWeight.SemiBold
                            ),
                            color = AppMuted
                        )
                    }
                    innerTextField()
                }
            )
        }
    }
}

private fun anchoredDropdownPositionProvider(): PopupPositionProvider {
    return object : PopupPositionProvider {
        override fun calculatePosition(
            anchorBounds: IntRect,
            windowSize: IntSize,
            layoutDirection: LayoutDirection,
            popupContentSize: IntSize
        ): IntOffset {
            val x = anchorBounds.left.coerceIn(
                0,
                (windowSize.width - popupContentSize.width).coerceAtLeast(0)
            )
            val belowY = anchorBounds.bottom + 6
            val aboveY = anchorBounds.top - popupContentSize.height - 6
            val y = if (belowY + popupContentSize.height <= windowSize.height) {
                belowY
            } else {
                aboveY.coerceAtLeast(0)
            }
            return IntOffset(x, y)
        }
    }
}
